import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import classnames from 'classnames';
import { Checkbox } from '@app/form';
import classNames from 'classnames';
import { CustomSelectChoice, FilterableValue } from '@app/api';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { Tooltip } from '@app/core';
import { useTranslation } from 'react-i18next';

interface Props {
  oldValues?: FilterableValue[] | undefined;
  handleCheckboxChange: (value: string, checked: boolean) => void;
  isChecked: (value: string) => boolean | undefined;
  hasSelection?: boolean;
  displayOther?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std * 2,
    padding: `${theme.marginBase}px ${theme.marginBase}px !important`,
  },
  listItem: {
    padding: 0,
    cursor: 'pointer',
  },
  choiceContainer: {
    marginTop: theme.marginBase / 2,
    marginBottom: theme.marginBase / 2,
    marginLeft: theme.marginBase / 2,
    '& .checkbox-label': {
      ...theme.fonts.caption,
      color: theme.colors.inkSecondary,
      '&:hover': {
        color: theme.colors.inkPrimary,
      },
    },
  },
  choiceContainerChecked: {
    '& .checkbox-label': {
      color: `${theme.colors.blueAccessible} !important`,
    },
  },
  accordion: {
    border: 'none',
    boxShadow: 'none !important',
    '&::before': {
      display: 'none',
    },
  },
  accordionSummaryContentWrapper: {
    margin: '0 !important',
    display: 'flex',
    alignItems: 'center',
  },
  accordionSummaryContainer: {
    width: '100%',
    padding: '0 !important',
    minHeight: `${theme.marginBase * 4}px !important`,
    height: `${theme.marginBase * 4}px !important`,
    '&.Mui-expanded': {
      minHeight: `${theme.marginBase * 4}px !important`,
      height: `${theme.marginBase * 4}px !important`,
    },
  },
  accordionSummaryContent: {
    ...theme.fonts.label,
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase / 2,
    color: theme.colors.inkTertiary,
    margin: '0 !important',
    width: '100%',
    flexGrow: 1,
    '&.Mui-expanded': {
      margin: '0 !important',
    },
  },
  icon: {
    fontSize: theme.iconSize.small,
    color: theme.colors.inkTertiary,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  helpIcon: {
    marginLeft: 'auto',
  },
}));

export const OldValuesList = ({
  oldValues,
  handleCheckboxChange,
  isChecked,
  hasSelection = false,
  displayOther = false,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isManuelExpandRef = useRef(false);

  const values = useMemo(() => {
    const hasOther = oldValues?.some(
      (v) => v.value === CustomSelectChoice.Other,
    );
    const withoutOther = oldValues?.filter(
      (v) => v.value !== CustomSelectChoice.Other,
    );

    if (!displayOther || !hasOther) {
      return withoutOther;
    }

    return [
      { value: CustomSelectChoice.Other, label: t('screeningForm.other') },
      ...(withoutOther || []),
    ];
  }, [displayOther, oldValues, t]);

  useEffect(() => {
    if (hasSelection && values) {
      setExpanded(true);
    }
  }, [values, hasSelection]);

  const handleAccordionExpand = (
    event: React.SyntheticEvent,
    isExpanded: boolean,
  ) => {
    setExpanded(isExpanded);

    if (isExpanded && containerRef.current) {
      isManuelExpandRef.current = true;
      setTimeout(() => {
        if (isManuelExpandRef.current && containerRef.current) {
          containerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
          isManuelExpandRef.current = false;
        }
      }, 100);
    }
  };

  if (!values || values.length === 0) {
    return null;
  }

  return (
    <Accordion
      ref={containerRef}
      disableGutters
      expanded={expanded}
      onChange={handleAccordionExpand}
      className={classes.accordion}
    >
      <AccordionSummary
        aria-controls="old-values-content"
        id="old-values-header"
        expandIcon={null}
        classes={{
          root: classes.accordionSummaryContainer,
          content: classes.accordionSummaryContentWrapper,
        }}
      >
        <div className={classes.accordionSummaryContent}>
          <i
            className={classnames(
              expanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line',
              classes.icon,
            )}
          ></i>
          <span>
            {t('sessions.details.participationsDetails.fieldFilter.oldValues')}
          </span>
          <Tooltip
            title={t(
              'sessions.details.participationsDetails.fieldFilter.oldValuesTooltip',
            )}
            placement="top"
            arrow
          >
            <i
              className={classnames(
                'ri-question-line',
                classes.icon,
                classes.helpIcon,
              )}
            />
          </Tooltip>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.container}>
        {values
          ?.slice()
          .sort((a, b) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
          )
          .map((v) => {
            const choiceValue = v.label;
            return (
              <div className={classes.listItem}>
                <Checkbox
                  containerClass={classNames(classes.choiceContainer, {
                    [classes.choiceContainerChecked]: isChecked(choiceValue),
                  })}
                  checked={isChecked(choiceValue)}
                  noMargin
                  onChange={(e) =>
                    handleCheckboxChange(choiceValue, e.target.checked)
                  }
                >
                  <span>{v.label}</span>
                </Checkbox>
              </div>
            );
          })}
      </AccordionDetails>
    </Accordion>
  );
};
